import React from 'react';
import { Box, Chip, Divider, Tooltip } from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  // GridToolbarDensitySelector,
  // GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { useStyles } from './BrokerSharing';

/**
 * Custom toolbar for the data grid
 */
export const CustomToolbar = () => {
  return (
    <GridToolbarContainer sx={{ paddingTop: 2, paddingX: 2 }}>
      <GridToolbarColumnsButton
        slotProps={{
          button: {
            variant: 'contained',
            sx: {
              backgroundColor: '#131C7A',
            },
          },
        }}
      />
      <GridToolbarFilterButton
        slotProps={{
          button: {
            variant: 'contained',
            sx: {
              backgroundColor: '#131C7A',
            },
          },
        }}
      />
      {/* Disabling until we get feedback that we want it */}
      {/* <GridToolbarDensitySelector
        slotProps={{ tooltip: { title: 'Change density' } }}
      /> */}
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter
        placeholder="Search carrier data"
        sx={{
          '& input': {
            backgroundColor: 'white',
            borderRadius: '4px',
            width: '240px',
          },
        }}
      />
      {/* <GridToolbarExport
        slotProps={{
          tooltip: { title: 'Export data' },
          button: { variant: 'contained' },
        }}
      /> */}
      <Divider />
    </GridToolbarContainer>
  );
};

const Wrapper = (props: { show: boolean; children: React.ReactElement }) => {
  return props.show ? (
    <Tooltip title="Click to reveal">{props.children}</Tooltip>
  ) : (
    <>{props.children}</>
  );
};

/**
 * Custom cell component for the data grid
 */
export const CellComponent = (props: {
  value: any;
  type: 'phone' | 'email';
}) => {
  /* STATE */
  const [isBlurred, setIsBlurred] = React.useState(true);
  const href =
    props.type === 'phone' ? `tel:${props.value}` : `mailto:${props.value}`;

  /* HOOKS */
  const classes = useStyles();

  /* FUNCTIONS */
  const onClick = (event: React.MouseEvent) => {
    if (isBlurred) {
      event.preventDefault();
      setIsBlurred(false);
    }
  };

  /* RENDER */
  if (!props.value || props.value === '' || props.value === '-') {
    return <span>-</span>;
  }

  return (
    <Wrapper show={isBlurred}>
      <Box
        className={` ${isBlurred ? classes.blurredText : ''}`}
        sx={{
          cursor: 'pointer',
          height: '100%',
          flex: 1,
          alignContent: 'center',
        }}
        onClick={onClick}>
        <a href={href} className={classes.link}>
          {props.value}
        </a>
      </Box>
    </Wrapper>
  );
};

enum EquipmentType {
  DRY_VAN = 'Dry Van',
  REEFER = 'Reefer',
  FLAT_BED = 'Flatbed',
  POWER_ONLY = 'Power Only',
  SPECIALIZED = 'Specialized',
}

// Color assignment for equipment types using the colors array and the enum
enum EquipmentTypeColors {
  DRY_VAN = '#FFD1DC',
  REEFER = '#FFF1CC',
  FLAT_BED = '#DFFFD6',
  POWER_ONLY = '#CCE0FF',
  SPECIALIZED = '#FFCCF9',
}

const getColor = (chip: string) => {
  // Check if chip is a key in EquipmentType
  if (Object.keys(EquipmentType).includes(chip)) {
    return EquipmentTypeColors[chip as keyof typeof EquipmentTypeColors];
  }
  // Check if chip is a value in EquipmentType
  const key = Object.keys(EquipmentType).find(
    (key) => EquipmentType[key as keyof typeof EquipmentType] === chip
  );
  if (key) {
    return EquipmentTypeColors[key as keyof typeof EquipmentTypeColors];
  }
  // Return default color if chip is neither a key nor a value in EquipmentType
};

/**
 * Custom cell component for the data grid that displays chips
 */
export const ChipsCellComponent = (props: { value: Array<string> }) => {
  /* RENDER */
  if (props.value?.length === 0 || !props.value) {
    return <span>-</span>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        alignItems: 'center',
        marginY: 1,
      }}>
      {props.value.map((chip, index) => {
        const equipmentType = Object.keys(EquipmentType).includes(chip)
          ? EquipmentType[chip as keyof typeof EquipmentType]
          : chip;
        return (
          <Chip
            key={index}
            label={equipmentType}
            sx={{
              backgroundColor: getColor(chip),
            }}
          />
        );
      })}
    </Box>
  );
};
